import { useEffect, useRef, useState } from 'react';
import { useAppStore, clearSession, GAME_SCREENS } from './store/useAppStore';
import { setOnUnauthorized } from './api/client';
import { referralApi } from './api/referral';
import { publicApi, PublicReplay } from './api/public';
import { setBotUsername } from './lib/appConfig';
import { SharedReplay } from './screens/SharedReplay';
import { telegram } from './lib/telegram';
import { formatTon } from './lib/format';
import { getLiveSocket } from './lib/live';
import { Icon } from './components/Icon';
import { GameTabs } from './components/GameTabs';
import { DiceScreen } from './screens/DiceScreen';
import { CrashScreen } from './screens/CrashScreen';
import { RouletteScreen } from './screens/RouletteScreen';
import { SlotsHub } from './screens/SlotsHub';
import { FairnessScreen } from './screens/FairnessScreen';
import { ReferralScreen } from './screens/ReferralScreen';
import { TransferScreen } from './screens/TransferScreen';
import { TopScreen } from './screens/TopScreen';
import { ProfileScreen } from './screens/ProfileScreen';
import { Toast } from './components/Toast';
import { LiveTicker } from './components/LiveTicker';
import { BigWinBanner } from './components/BigWinBanner';
import { BottomNav } from './components/BottomNav';

function ensureProfileId(): string {
  const existing = localStorage.getItem('raketta_profile');
  if (existing) return existing;
  const pid: string =
    (window.crypto as any)?.randomUUID?.() ??
    `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem('raketta_profile', pid);
  return pid;
}

export default function App() {
  const authLoading = useAppStore((s) => s.authLoading);
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const balance = useAppStore((s) => s.balance);
  const screen = useAppStore((s) => s.screen);
  const isGame = GAME_SCREENS.includes(screen);
  const authenticate = useAppStore((s) => s.authenticate);
  const devLogin = useAppStore((s) => s.devLogin);
  const restoreSession = useAppStore((s) => s.restoreSession);
  const pushFeed = useAppStore((s) => s.pushFeed);
  const triggerBigWin = useAppStore((s) => s.triggerBigWin);
  const refApplied = useRef(false);
  const [shared, setShared] = useState<PublicReplay | null>(null);
  const replayChecked = useRef(false);

  // Имя бота для дип-линков реплеев
  useEffect(() => {
    publicApi.config().then((c) => setBotUsername(c.botUsername)).catch(() => undefined);
  }, []);

  // Зашли по ссылке t.me/bot?startapp=replay_<id> — открываем чужой реплей
  useEffect(() => {
    if (!isAuthenticated || replayChecked.current) return;
    const sp = telegram.startParam();
    const m = sp ? /^replay_(\d+)$/.exec(sp) : null;
    if (m) {
      replayChecked.current = true;
      publicApi.replay(Number(m[1])).then(setShared).catch(() => undefined);
    }
  }, [isAuthenticated]);

  // Реферал: если зашли по ссылке t.me/bot?startapp=ref_<id> — применяем (бонус обоим)
  useEffect(() => {
    if (!isAuthenticated || refApplied.current) return;
    const sp = telegram.startParam();
    if (sp && /^ref_\d+$/.test(sp)) {
      refApplied.current = true;
      referralApi
        .apply(sp)
        .then((r) => {
          if (r.applied) {
            useAppStore.getState().setBalance(r.newBalance);
            useAppStore.getState().showToast(`Бонус за приглашение +${r.bonus}`);
          }
        })
        .catch(() => undefined);
    }
  }, [isAuthenticated]);

  // Общая live-лента: подключаемся после авторизации
  useEffect(() => {
    if (!isAuthenticated) return;
    const s = getLiveSocket();
    const onBet = (item: any) => pushFeed(item);
    const onBig = (item: any) => triggerBigWin(item);
    s.on('feed:bet', onBet);
    s.on('feed:bigwin', onBig);
    return () => {
      s.off('feed:bet', onBet);
      s.off('feed:bigwin', onBig);
    };
  }, [isAuthenticated, pushFeed, triggerBigWin]);

  useEffect(() => {
    telegram.init();

    const reauth = () => {
      const initData = telegram.initData();
      if (initData) authenticate(initData);
      else if (import.meta.env.DEV) devLogin(ensureProfileId());
      else useAppStore.setState({ authLoading: false });
    };

    // Протух токен — чистим сессию и заходим заново
    setOnUnauthorized(() => {
      clearSession();
      reauth();
    });

    const initData = telegram.initData();
    if (initData) {
      // В Telegram всегда свежий initData (он же сохранит сессию)
      authenticate(initData);
    } else if (restoreSession()) {
      // Мгновенно восстановили сохранённую сессию
    } else {
      reauth();
    }
  }, [authenticate, devLogin, restoreSession]);

  if (authLoading) {
    return (
      <div className="center-state">
        <span className="spin" style={{ width: 28, height: 28, borderColor: 'rgba(255,255,255,0.2)', borderTopColor: 'var(--green)' }} />
        <p>Загрузка профиля…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="center-state">
        <h1>1Zov</h1>
        <p>Открой приложение из Telegram, чтобы войти в свой профиль.</p>
        <p className="hint">Provably fair · TON testnet</p>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="screen has-nav">
        <div className="topbar">
          <div className="brand">
            <span className="logo">
              <Icon name="bolt" size={15} />
            </span>
            1Zov
          </div>
          <div className="balance-pill">
            <span className="gem">
              <Icon name="gem" size={14} />
            </span>
            <span className="amt tnum">{formatTon(balance)}</span>
          </div>
        </div>

        {shared ? (
          <SharedReplay replay={shared} onClose={() => setShared(null)} />
        ) : (
          <>
            {isGame && <LiveTicker />}
            {isGame && <GameTabs />}

            {screen === 'dice' && <DiceScreen />}
            {screen === 'crash' && <CrashScreen />}
            {screen === 'roulette' && <RouletteScreen />}
            {screen === 'slots' && <SlotsHub />}
            {screen === 'top' && <TopScreen />}
            {screen === 'profile' && <ProfileScreen />}
            {screen === 'transfer' && <TransferScreen />}
            {screen === 'referral' && <ReferralScreen />}
            {screen === 'fairness' && <FairnessScreen />}
          </>
        )}
      </div>

      {!shared && <BottomNav />}
      <BigWinBanner />
      <Toast />
    </div>
  );
}
